import * as yup from 'yup';
import {
  BusinessType,
  EmployeeCountRange,
  CompanyRegistrationData,
} from '../types/company';
import { PREFECTURES } from '../constants/prefectures';

// メールアドレス正規表現
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// パスワード正規表現（英数字を含む）
const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*\d)/;

// 前後空白禁止の正規表現
const NO_LEADING_TRAILING_SPACES = /^[^\s].+[^\s]$/;

// 都道府県名の配列
const PREFECTURE_NAMES = PREFECTURES.map((p) => p.name);

// 企業登録バリデーションスキーマ
export const companyRegistrationSchema = yup.object({
  companyName: yup
    .string()
    .required('企業名は必須です')
    .max(100, '企業名は100文字以下で入力してください')
    .matches(NO_LEADING_TRAILING_SPACES, '前後の空白は削除してください')
    .test('no-test-company', '適切な企業名を入力してください', (value) => {
      if (!value) return true;
      const ngWords = ['テスト', 'test', '株式会社株式会社'];
      return !ngWords.some((ng) =>
        value.toLowerCase().includes(ng.toLowerCase())
      );
    }),

  representativeName: yup
    .string()
    .required('代表者氏名は必須です')
    .max(50, '代表者氏名は50文字以下で入力してください')
    .matches(NO_LEADING_TRAILING_SPACES, '前後の空白は削除してください'),

  prefecture: yup
    .string()
    .required('所在地（都道府県）を選択してください')
    .oneOf(PREFECTURE_NAMES, '有効な都道府県を選択してください'),

  city: yup
    .string()
    .required('所在地（市区町村）は必須です')
    .max(50, '市区町村は50文字以下で入力してください'),

  businessType: yup
    .string()
    .required('業種を選択してください')
    .oneOf(Object.values(BusinessType), '有効な業種を選択してください'),

  employeeCountRange: yup
    .string()
    .required('従業員数規模を選択してください')
    .oneOf(
      Object.values(EmployeeCountRange),
      '有効な従業員数規模を選択してください'
    ),

  contactEmail: yup
    .string()
    .required('メールアドレスは必須です')
    .max(255, 'メールアドレスは255文字以下で入力してください')
    .matches(EMAIL_REGEX, '正しいメールアドレスを入力してください')
    .test(
      'no-duplicate-email',
      'このメールアドレスは既に登録されています',
      (value) => {
        // フロントエンドでは重複チェックはしない（サーバーサイドで実施）
        return true;
      }
    ),

  password: yup
    .string()
    .required('パスワードは必須です')
    .min(8, 'パスワードは8文字以上で入力してください')
    .matches(PASSWORD_REGEX, '英数字を含むパスワードを設定してください'),

  confirmPassword: yup
    .string()
    .required('パスワード確認は必須です')
    .oneOf([yup.ref('password')], 'パスワードが一致しません'),

  agreedToTerms: yup
    .boolean()
    .required('利用規約への同意が必要です')
    .oneOf([true], '利用規約への同意が必要です'),

  agreedToPrivacy: yup
    .boolean()
    .required('プライバシーポリシーへの同意が必要です')
    .oneOf([true], 'プライバシーポリシーへの同意が必要です'),
});

// バリデーション実行関数
export const validateCompanyRegistration = async (
  data: CompanyRegistrationData
): Promise<{ isValid: boolean; errors: Record<string, string> }> => {
  try {
    await companyRegistrationSchema.validate(data, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: Record<string, string> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { isValid: false, errors };
    }
    return {
      isValid: false,
      errors: { general: '予期しないエラーが発生しました' },
    };
  }
};

// 単一フィールドのバリデーション
export const validateField = async (
  fieldName: keyof CompanyRegistrationData,
  value: any,
  allData?: Partial<CompanyRegistrationData>
): Promise<string | null> => {
  try {
    await (yup.reach(companyRegistrationSchema, fieldName) as any).validate(
      value
    );
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return '入力内容を確認してください';
  }
};

// パスワード強度チェック
export const getPasswordStrength = (
  password: string
): {
  score: number;
  feedback: string[];
} => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('8文字以上にしてください');
  }

  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('小文字を含めてください');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('大文字を含めてください');
  }

  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('数字を含めてください');
  }

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
    feedback.unshift('記号を含めるとより安全です');
  }

  return { score, feedback };
};
